import { jsPDF } from "jspdf";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cabvalley-ride/artifacts/2vw1iw9l_Untitled%20design-14.png";
const BRAND_GREEN = [27, 67, 50];
const BRAND_GOLD = [212, 160, 23];
const GREY = [100, 116, 139];
const DARK = [15, 23, 42];
const LIGHT_BG = [248, 250, 252];

function drawLine(doc, y, x1 = 20, x2 = 190) {
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.3);
  doc.line(x1, y, x2, y);
}

function addRow(doc, y, label, value, opts = {}) {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...GREY);
  doc.text(label, 25, y);
  doc.setFont("helvetica", opts.bold ? "bold" : "normal");
  doc.setFontSize(opts.bold ? 10 : 9);
  doc.setTextColor(...DARK);
  doc.text(String(value || "-"), 185, y, { align: "right" });
  return y + 7;
}

async function loadImageAsBase64(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

export async function generateInvoicePDF(data) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  // Header background
  doc.setFillColor(...BRAND_GREEN);
  doc.rect(0, 0, 210, 45, "F");

  // Try to load logo
  try {
    const logoBase64 = await loadImageAsBase64(LOGO_URL);
    if (logoBase64) doc.addImage(logoBase64, "PNG", 20, 8, 28, 28);
  } catch (e) { console.error("Failed to load invoice logo:", e); }

  // Brand name
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(255, 255, 255);
  doc.text("Cab Valley", 55, 22);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(255, 255, 255, 180);
  doc.text("Premium Ride Services", 55, 29);

  // Invoice label
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(255, 255, 255);
  doc.text("TAX INVOICE", 185, 18, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(data.invoice_id || "", 185, 24, { align: "right" });
  doc.text(data.created_at ? new Date(data.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "", 185, 30, { align: "right" });

  let y = 55;

  // Booking details section
  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(20, y - 5, 170, 30, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND_GREEN);
  doc.text("BOOKING DETAILS", 25, y + 2);
  y += 9;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...DARK);
  doc.text(`Booking ID: ${data.ride_id || ""}`, 25, y);
  doc.text(`Service: ${(data.service_type || "").replace(/_/g, " ")}`, 110, y);
  y += 6;
  doc.text(`Date: ${data.created_at ? new Date(data.created_at).toLocaleString("en-IN") : "-"}`, 25, y);
  doc.text(`Status: ${data.ride_status || ""}`, 110, y);

  y += 18;

  // Passenger & Driver
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND_GREEN);
  doc.text("TRIP INFORMATION", 25, y);
  y += 8;

  y = addRow(doc, y, "Passenger Name", data.customer_name);
  y = addRow(doc, y, "Passenger Phone", data.customer_phone || "-");
  y = addRow(doc, y, "Driver Name", data.driver_name || "-");
  drawLine(doc, y - 2);
  y += 3;
  y = addRow(doc, y, "Pickup Location", data.pickup_location);
  y = addRow(doc, y, "Drop Location", data.drop_location);

  y += 5;
  drawLine(doc, y);
  y += 8;

  // Payment section
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND_GREEN);
  doc.text("PAYMENT DETAILS", 25, y);
  y += 8;

  y = addRow(doc, y, "Payment Method", (data.payment_method || "cash").toUpperCase());
  y = addRow(doc, y, "Payment Status", (data.payment_status || "pending").replace(/_/g, " "));
  if (data.upi_reference) {
    y = addRow(doc, y, "UTR / Reference", data.upi_reference);
  }

  // Passenger info
  if (data.passenger_count && data.passenger_count > 1) {
    y += 3;
    drawLine(doc, y);
    y += 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND_GREEN);
    doc.text("PASSENGER DETAILS", 25, y);
    y += 8;
    y = addRow(doc, y, "Total Passengers", String(data.passenger_count));
    if (data.passenger_names && data.passenger_names.length > 0) {
      data.passenger_names.forEach((name, i) => {
        if (name) y = addRow(doc, y, `Passenger ${i + 2}`, name);
      });
    }
  }

  y += 3;

  // Total box
  doc.setFillColor(...BRAND_GREEN);
  doc.roundedRect(20, y, 170, 18, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.text("TOTAL AMOUNT", 25, y + 12);
  doc.setFontSize(16);
  doc.text(`Rs. ${data.fare_amount || 0}`, 185, y + 12, { align: "right" });

  y += 28;

  // Footer
  drawLine(doc, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...GREY);
  const platform = data.platform_name || "Cab Valley";
  doc.text(`${platform} | ${data.support_email || ""} | ${data.support_phone || ""}`, 105, y, { align: "center" });
  y += 4;
  doc.text(data.business_address || "", 105, y, { align: "center" });
  y += 4;
  doc.text("This is a computer-generated invoice. No signature required.", 105, y, { align: "center" });

  // Save
  const filename = `CabValley_Invoice_${(data.ride_id || "unknown").replace("ride_", "")}.pdf`;
  doc.save(filename);
}

export async function generateReceiptPDF(data) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  // Header background
  doc.setFillColor(...BRAND_GREEN);
  doc.rect(0, 0, 210, 40, "F");

  try {
    const logoBase64 = await loadImageAsBase64(LOGO_URL);
    if (logoBase64) doc.addImage(logoBase64, "PNG", 20, 6, 26, 26);
  } catch (e) { console.error("Failed to load receipt logo:", e); }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(255, 255, 255);
  doc.text("Cab Valley", 52, 20);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Premium Ride Services", 52, 27);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("PAYMENT RECEIPT", 185, 16, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(data.receipt_id || "", 185, 23, { align: "right" });
  doc.text(data.payment_date ? new Date(data.payment_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "", 185, 29, { align: "right" });

  let y = 52;

  // Trip summary
  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(20, y - 5, 170, 22, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND_GREEN);
  doc.text("TRIP SUMMARY", 25, y + 2);
  y += 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...DARK);
  doc.text(`Booking: ${data.ride_id || ""}`, 25, y);
  doc.text(`Service: ${(data.service_type || "").replace(/_/g, " ")}`, 110, y);

  y += 16;

  y = addRow(doc, y, "Customer", data.customer_name);
  y = addRow(doc, y, "Driver", data.driver_name || "-");
  y = addRow(doc, y, "Pickup", data.pickup);
  y = addRow(doc, y, "Drop", data.drop);
  drawLine(doc, y - 2);
  y += 4;
  y = addRow(doc, y, "Payment Method", (data.payment_method || "cash").toUpperCase());
  y = addRow(doc, y, "Payment Status", (data.payment_status || "").replace(/_/g, " "));
  if (data.upi_reference) {
    y = addRow(doc, y, "UTR / Reference", data.upi_reference);
  }
  if (data.passenger_count && data.passenger_count > 1) {
    y = addRow(doc, y, "Passengers", String(data.passenger_count));
    if (data.passenger_names && data.passenger_names.length > 0) {
      data.passenger_names.forEach((name, i) => {
        if (name) y = addRow(doc, y, `Passenger ${i + 2}`, name);
      });
    }
  }

  y += 4;

  // Total
  doc.setFillColor(...BRAND_GREEN);
  doc.roundedRect(20, y, 170, 16, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text("AMOUNT PAID", 25, y + 11);
  doc.setFontSize(15);
  doc.text(`Rs. ${data.amount_paid || 0}`, 185, y + 11, { align: "right" });

  y += 26;
  drawLine(doc, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...GREY);
  doc.text("Thank you for choosing Cab Valley. This is a computer-generated receipt.", 105, y, { align: "center" });

  const filename = `CabValley_Receipt_${(data.ride_id || "unknown").replace("ride_", "")}.pdf`;
  doc.save(filename);
}
